// Cave84 — Market Price Refresh (Phase 4 Week 13 → simplified 2026-07-01)
// Spec §4.7 F7.6. Price refresh is now Cloud Function-driven end-to-end:
//   - New vintages get an instant, automatic price lookup
//     (autoRefreshNewVintagePrice trigger, apps/functions/src/index.ts).
//   - detectStalePrices (monthly cron) flags vintages not priced in 12+ months.
//   - This page covers the two owner-facing gaps that automation doesn't:
//     1. Any vintage that still has no price at all (rare — catches edge cases).
//     2. An occasional manual "refresh anything stale" nudge.
// No CLI / terminal step. Every "Queue" action here writes a priority='high'
// generation_queue entry, which the Cloud Function picks up within seconds.
// Bulk stale-refresh is capped per click (cost-discipline: no web-search
// token bomb on large batches — see docs/CLAUDE.md).

import { useState, useMemo } from 'react';
import { useAllVintages } from '@/hooks/useVintages';
import { useWines } from '@/hooks/useWines';
import { useProducers } from '@/hooks/useProducers';
import {
  useEnqueuePriceRefresh,
  useForceRequeuePriceRefresh,
  usePriceRefreshQueue,
} from '@/hooks/useMarketPrices';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const STALE_BATCH_CAP = 50; // cost-discipline: honor a small limit per click

// A queue entry sitting in pending/processing longer than this is treated as
// stuck (normal runs complete in ~10-20s per vintage — see run-price-refresh.ts)
// and offered a one-click retry instead of an indefinite "Queued…" label.
const STUCK_THRESHOLD_MS = 90 * 1000;

function toDateSafe(ts: { toDate?: () => Date } | undefined): Date {
  return ts?.toDate?.() ?? new Date(ts as unknown as string);
}

/** ISO date string N days ago */
function daysAgoISO(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

function ProgressRow({ label, count, color }: { label: string; count: number; color: string }) {
  if (count === 0) return null;
  return (
    <div className="flex items-center gap-3">
      <span className={'w-2 h-2 rounded-full flex-shrink-0 ' + color} />
      <span className="text-sm text-text-secondary flex-1">{label}</span>
      <span className="text-sm font-mono text-text-primary tabular-nums">{count}</span>
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function PriceRefreshWizardPage() {
  const { data: allVintages = [], isLoading: loadingVintages } = useAllVintages();
  const { data: wines = [] } = useWines();
  const { data: producers = [] } = useProducers();
  const { data: queueEntries = [] } = usePriceRefreshQueue();
  const enqueueMutation = useEnqueuePriceRefresh();
  const forceRequeueMutation = useForceRequeuePriceRefresh();

  // ── No-price panel state ─────────────────────────────────────────────────
  const [noPricePanelOpen, setNoPricePanelOpen] = useState(false);

  // ── Stale-refresh state ──────────────────────────────────────────────────
  const [staleDate, setStaleDate] = useState<string>(daysAgoISO(365));

  // Wine name / producer lookups
  const wineNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of wines) m.set(w.id, w.cuvee_name ?? w.id);
    return m;
  }, [wines]);

  const producerNameMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of producers) m.set(p.id, p.name);
    return m;
  }, [producers]);

  const wineProducerMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const w of wines) m.set(w.id, w.producer_id ?? '');
    return m;
  }, [wines]);

  const wineRegionMap = useMemo(() => new Map<string, string>(), []); // unused now, kept for enqueuePriceRefresh signature

  // ── Active vintages (in cellar) ──────────────────────────────────────────
  const activeVintages = useMemo(
    () => allVintages.filter((v) => v.bottle_count > 0),
    [allVintages]
  );

  // ── Vintages with no price ever ──────────────────────────────────────────
  const noPriceVintages = useMemo(
    () => activeVintages.filter((v) => !v.price_last_refreshed_at),
    [activeVintages]
  );

  // ── Stale vintages (priced, but before cutoff) ───────────────────────────
  const staleCutoff = useMemo(() => new Date(staleDate), [staleDate]);
  const staleVintages = useMemo(
    () =>
      activeVintages.filter((v) => {
        if (!v.price_last_refreshed_at) return false; // counted in noPriceVintages instead
        const refreshedAt =
          v.price_last_refreshed_at.toDate?.() ?? new Date(v.price_last_refreshed_at as unknown as string);
        return refreshedAt < staleCutoff;
      }),
    [activeVintages, staleCutoff]
  );

  // ── Queue stats (live status of everything in flight) ───────────────────
  const queueStats = useMemo(() => {
    let pending = 0, processing = 0, completed = 0, failed = 0, review = 0;
    for (const e of queueEntries) {
      if (e.status === 'pending')                pending++;
      else if (e.status === 'processing')         processing++;
      else if (e.status === 'completed')          completed++;
      else if (e.status === 'failed')             failed++;
      else if (e.status === 'needs_owner_review') review++;
    }
    return { pending, processing, completed, failed, review, total: queueEntries.length };
  }, [queueEntries]);

  const activeQueueEntryFor = (vintageId: string) =>
    queueEntries.find((e) => e.entity_id === vintageId && (e.status === 'pending' || e.status === 'processing'));

  const isQueued = (vintageId: string) => !!activeQueueEntryFor(vintageId);

  // Stuck = has been sitting pending/processing past the normal processing
  // window (e.g. the Cloud Function trigger silently dropped). Surfaces a
  // retry action instead of leaving the owner staring at "Queued…" forever.
  const isStuck = (vintageId: string) => {
    const entry = activeQueueEntryFor(vintageId);
    if (!entry) return false;
    return Date.now() - toDateSafe(entry.requested_at).getTime() > STUCK_THRESHOLD_MS;
  };

  const stuckNoPriceIds = useMemo(
    () => noPriceVintages.filter((v) => isStuck(v.id)).map((v) => v.id),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [noPriceVintages, queueEntries]
  );

  const handleQueueOne = (vintageId: string) => {
    enqueueMutation.mutate({
      vintages: activeVintages,
      scope: { type: 'vintage_ids', ids: [vintageId] },
      wineRegionMap,
      wineProducerMap,
      priority: 'high',
    });
  };

  // Owner self-heal: force-clears the stuck queue entry(ies) and writes
  // fresh priority='high' ones so the Cloud Function trigger fires again.
  const handleForceRequeue = (vintageIds: string[]) => {
    if (vintageIds.length === 0) return;
    forceRequeueMutation.mutate(vintageIds);
  };

  const handleQueueStale = () => {
    const ids = staleVintages.slice(0, STALE_BATCH_CAP).map((v) => v.id);
    if (ids.length === 0) return;
    enqueueMutation.mutate({
      vintages: activeVintages,
      scope: { type: 'vintage_ids', ids },
      wineRegionMap,
      wineProducerMap,
      priority: 'high',
    });
  };

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-surface-default text-text-primary">

      {/* Header */}
      <div className="border-b border-border-default bg-surface-elevated">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-5">
          <p className="text-xs text-text-secondary uppercase tracking-widest mb-1">Market Prices</p>
          <h1 className="text-2xl font-bold text-text-primary">Price Coverage</h1>
          <p className="text-sm text-text-secondary mt-1">
            New bottles get priced automatically. Use this page for anything still missing a
            price, or to nudge a refresh on prices that have gone stale.
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 space-y-8">

        {/* ── Wines without any price ──────────────────────────────────── */}
        {!loadingVintages && (
          <section>
            <button
              onClick={() => setNoPricePanelOpen((v) => !v)}
              className="w-full flex items-center justify-between bg-surface-elevated rounded-xl border border-border-default px-5 py-4 text-left hover:border-accent-burgundy/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className={
                  'w-5 h-5 rounded-full flex-shrink-0 ' +
                  (noPriceVintages.length > 0 ? 'bg-amber-400' : 'bg-emerald-400')
                } />
                <span className="text-sm font-semibold text-text-primary">
                  Wines without any price
                </span>
                <span className="text-sm text-text-secondary tabular-nums">
                  {noPriceVintages.length === 0
                    ? '— all priced ✓'
                    : `${noPriceVintages.length} vintage${noPriceVintages.length !== 1 ? 's' : ''}`}
                </span>
              </div>
              <svg
                className={'w-4 h-4 text-text-secondary transition-transform ' + (noPricePanelOpen ? 'rotate-180' : '')}
                fill="none" viewBox="0 0 24 24" stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {noPricePanelOpen && noPriceVintages.length > 0 && (
              <div className="mt-2 space-y-2">
                <button
                  onClick={() => handleForceRequeue(noPriceVintages.map((v) => v.id))}
                  disabled={forceRequeueMutation.isPending}
                  className="w-full px-5 py-2.5 rounded-lg bg-accent-burgundy hover:bg-accent-burgundy/80 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {forceRequeueMutation.isPending
                    ? 'Refreshing…'
                    : `Refresh all ${noPriceVintages.length} now`}
                </button>
                {stuckNoPriceIds.length > 0 && (
                  <p className="text-xs text-amber-400">
                    {stuckNoPriceIds.length} of these look stuck (queued over 90s with no result) —
                    this will clear and re-trigger them.
                  </p>
                )}

                <div className="bg-surface-elevated rounded-xl border border-border-default divide-y divide-border-default overflow-hidden">
                  {noPriceVintages.map((v) => {
                    const wineName = wineNameMap.get(v.wine_id) ?? '—';
                    const wine = wines.find((w) => w.id === v.wine_id);
                    const producerName = wine ? (producerNameMap.get(wine.producer_id ?? '') ?? '—') : '—';
                    const queued = isQueued(v.id);
                    const stuck = queued && isStuck(v.id);
                    return (
                      <div key={v.id} className="flex items-center justify-between px-5 py-3">
                        <div>
                          <p className="text-sm text-text-primary font-medium">{producerName} — {wineName} {v.year}</p>
                          <p className="text-xs text-text-secondary">{v.bottle_count} bottle{v.bottle_count !== 1 ? 's' : ''} in cellar</p>
                        </div>
                        {stuck ? (
                          <button
                            onClick={() => handleForceRequeue([v.id])}
                            disabled={forceRequeueMutation.isPending}
                            className="text-xs px-3 py-1.5 rounded-lg border border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black transition-colors disabled:opacity-40"
                          >
                            Stuck — Retry
                          </button>
                        ) : queued ? (
                          <span className="text-xs text-amber-400 font-medium">Queued…</span>
                        ) : (
                          <button
                            onClick={() => handleQueueOne(v.id)}
                            disabled={enqueueMutation.isPending}
                            className="text-xs px-3 py-1.5 rounded-lg border border-accent-burgundy text-accent-burgundy hover:bg-accent-burgundy hover:text-white transition-colors disabled:opacity-40"
                          >
                            Queue
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {noPricePanelOpen && noPriceVintages.length === 0 && (
              <div className="mt-2 bg-surface-elevated rounded-xl border border-border-default px-5 py-4">
                <p className="text-sm text-text-secondary">All active vintages have at least one price record.</p>
              </div>
            )}
          </section>
        )}

        {/* ── Occasional stale-price refresh ───────────────────────────── */}
        <section>
          <h2 className="text-base font-semibold text-text-primary mb-4">Refresh stale prices</h2>
          <div className="bg-surface-elevated rounded-xl border border-border-default p-5 space-y-4">
            <div>
              <span className="block text-xs text-text-secondary uppercase tracking-widest mb-1.5">
                Refresh vintages not updated since
              </span>
              <div className="flex items-center gap-3 flex-wrap">
                <input
                  type="date"
                  value={staleDate}
                  max={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setStaleDate(e.target.value)}
                  className="bg-surface-default border border-border-default rounded-lg px-3 py-2 text-sm text-text-primary focus:outline-none focus:border-accent-burgundy"
                />
                <div className="flex gap-1.5">
                  {[90, 180, 365].map((days) => (
                    <button
                      key={days}
                      onClick={() => setStaleDate(daysAgoISO(days))}
                      className="px-2 py-1 rounded text-xs border border-border-default text-text-secondary hover:border-accent-burgundy hover:text-text-primary transition-colors"
                    >
                      {days}d
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {!loadingVintages && (
              <p className="text-sm text-text-secondary">
                <span className="font-semibold text-text-primary">
                  {Math.min(staleVintages.length, STALE_BATCH_CAP)}
                </span>{' '}
                of <span className="text-text-primary">{staleVintages.length}</span> stale vintage
                {staleVintages.length !== 1 ? 's' : ''} will be queued
                {staleVintages.length > STALE_BATCH_CAP && ` (capped at ${STALE_BATCH_CAP} per click — run again for the rest)`}.
              </p>
            )}

            <button
              onClick={handleQueueStale}
              disabled={enqueueMutation.isPending || staleVintages.length === 0}
              className="px-5 py-2.5 rounded-lg bg-accent-burgundy hover:bg-accent-burgundy/80 text-white text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {enqueueMutation.isPending
                ? 'Queuing…'
                : `Queue ${Math.min(staleVintages.length, STALE_BATCH_CAP)} for refresh`}
            </button>

            <p className="text-xs text-text-secondary">
              Runs automatically in the background (~20 sec per vintage). A monthly check also
              flags anything not refreshed in over a year.
            </p>
          </div>
        </section>

        {/* ── Queue Progress ───────────────────────────────────────────── */}
        {queueStats.total > 0 && (queueStats.pending > 0 || queueStats.processing > 0 || queueStats.review > 0 || queueStats.failed > 0) && (
          <section>
            <h2 className="text-base font-semibold text-text-primary mb-4">In progress</h2>
            <div className="bg-surface-elevated rounded-xl border border-border-default p-5 space-y-3">
              <ProgressRow label="Pending"      count={queueStats.pending}    color="bg-amber-400" />
              <ProgressRow label="Processing"   count={queueStats.processing} color="bg-sky-400" />
              <ProgressRow label="Needs review" count={queueStats.review}     color="bg-orange-400" />
              <ProgressRow label="Failed"       count={queueStats.failed}     color="bg-red-400" />
              {queueStats.review > 0 && (
                <a href="/prices/review" className="inline-flex items-center gap-2 mt-2 text-sm text-accent-burgundy hover:underline">
                  Review {queueStats.review} item{queueStats.review !== 1 ? 's' : ''} →
                </a>
              )}
            </div>
          </section>
        )}

      </div>
    </div>
  );
}
